// frontend/src/features/ups/ManageInternalDrivers.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'; 

import {
  fetchInternalDrivers,
  createInternalDriver,
  updateInternalDriver,
  deleteInternalDriver,
  reserveDriver,   
  activateDriver,  
  unbanDriver,     
} from '../../services/upsDriverService';

// New Modals & Selectors
import InternalDriverFormModal from './modals/InternalDriverFormModal';
import DriverDetailsModal from './modals/DriverDetailsModal'; 

import {
  selectCompanySites,
  loadMyCompanySites,
  selectSitesLoading,
} from '../sites/sitesSlice'; // Matched to your provided sitesSlice names

// ==========================================
// SECTION 1: HELPER COMPONENTS
// A tiny, purely visual component for the table UI
// ==========================================
function SourceBadge({ workforceSource }) {
  const source = (workforceSource || 'COMPANY').toString().toUpperCase();
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs rounded border bg-blue-50 text-blue-700 border-blue-200">
      {source === 'COMPANY' ? 'Internal' : source}
    </span>
  );
}

function resolveInternalDriverState(row) {
  if (row.current_state) return String(row.current_state).toLowerCase();

  if (row.is_banned) return 'banned';

  const status = String(row.assignment_status || '').toLowerCase();

  if (status === 'active') return 'active';
  if (status === 'confirmed') return 'reserved';
  if (status === 'requested') return 'requested';

  return 'available';
}

function formatStateLabel(state) {
  switch (state) {
    case 'banned':
      return 'Banned';
    case 'active':
      return 'Active';
    case 'reserved':
      return 'Reserved';
    case 'requested':
      return 'Requested';
    default:
      return 'Available';
  }
}

function getCurrentSiteLabel(row) {
  if (row.site_name) return row.site_name;
  if (row.site_id) return `Site #${row.site_id}`;
  return '—';
}

function getPrimaryAction(row) {
  const state = resolveInternalDriverState(row);

  switch (state) {
    case 'available':
      return {
        label: 'Reserve',
        action: 'reserve',
        className:
          'bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50',
      };

    case 'reserved':
      return {
        label: 'Activate',
        action: 'activate',
        className:
          'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50',
      };

    case 'active':
      return {
        label: 'Reserve',
        action: 'reserve',
        className:
          'bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50',
      };

    case 'banned':
      return {
        label: 'Unban',
        action: 'unban',
        className:
          'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50',
      };

    default:
      return null;
  }
}

export default function ManageInternalDrivers() {
  // ==========================================
  // SECTION 2: STATE MANAGEMENT
  // ==========================================

  const dispatch = useDispatch();
  const sites = useSelector(selectCompanySites);
  const sitesLoading = useSelector(selectSitesLoading);
  const [siteFilter, setSiteFilter] = useState('all');
  const [dispatchSiteId, setDispatchSiteId] = useState('');
  
  // Data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search state
  const [query, setQuery] = useState('');

  // Modal control state
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [stateFilter, setStateFilter] = useState('all');
  const [detailsDriver, setDetailsDriver] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const isAliveRef = useRef(true);


  // ==========================================
  // SECTION 3: DATA FETCHING & FILTERING
  // ==========================================

  // Wraps the API call to fetch drivers so it can be reused (e.g., after an edit)
  const load = useCallback(async () => {
    if (!isAliveRef.current) return;

    setLoading(true);
    setError('');

    try {
      const data = await fetchInternalDrivers();
      if (!isAliveRef.current) return;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isAliveRef.current) return;
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to load internal drivers'
      );
    } finally {
      if (!isAliveRef.current) return;
      setLoading(false);
    }
  }, []); // <--- No longer dependent on the search box

  useEffect(() => {
    isAliveRef.current = true;
    return () => {
      isAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (sites.length > 0 || sitesLoading) return;
    dispatch(loadMyCompanySites());
  }, [dispatch, sites.length, sitesLoading]);

  // Triggers the initial load when the component mounts
  useEffect(() => {
    load();
  }, [load]);

const filtered = useMemo(() => {
  const q = query.trim().toLowerCase();

  return rows.filter((row) => {
    const resolvedState = resolveInternalDriverState(row);

    const matchesState =
      stateFilter === 'all' ? true : resolvedState === stateFilter;

    const matchesSite =
      !siteFilter || String(siteFilter) === 'all'
        ? true
        : String(row.site_id) === String(siteFilter);

    const matchesSearch =
      !q ||
      (row.name || '').toLowerCase().includes(q) ||
      (row.email || '').toLowerCase().includes(q) ||
      (row.phone || '').toLowerCase().includes(q) ||
      (row.vehicle_registration_number || '').toLowerCase().includes(q);

    return matchesState && matchesSite && matchesSearch;
  });
}, [rows, query, stateFilter, siteFilter]);


  // ==========================================
  // SECTION 4: EVENT HANDLERS (CRUD)
  // ==========================================

  const openDetails = (driver) => {
    setDetailsDriver(driver);
  };

  // Split into View Filter and Dispatch Target handlers
  const handleSiteFilterChange = (e) => {
    setSiteFilter(e.target.value);
  };

  const handleDispatchSiteChange = (e) => {
    setDispatchSiteId(e.target.value);
  };

  const handlePrimaryAction = async (driver) => {
    const state = resolveInternalDriverState(driver);
    setError('');
    setActionBusyId(driver.id);

    try {
      if (state === 'banned') {
        await unbanDriver(driver.ban_id || driver.id);
        await load();
        return;
      }

    // Now uses dispatchSiteId instead of siteFilter
    if (!dispatchSiteId) {
      setError('Select a dispatch site first.');
      return;
    }

    if (state === 'available' || state === 'active') {
      await reserveDriver(driver.id, dispatchSiteId);
      await load();
      return;
    }

    if (state === 'reserved') {
      await activateDriver(driver.id, dispatchSiteId);
      await load();
      return;
    }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to perform driver action'
      );
    } finally {
      setActionBusyId(null);
    }
  };

  const openCreate = () => {
    setEditingDriver(null);
    setShowForm(true);
  };

  const openEdit = (driver) => {
    setEditingDriver(driver);
    setShowForm(true);
  };

  const closeForm = () => {
    if (submitting) return; // Prevent closing while a network request is active
    setShowForm(false);
    setEditingDriver(null);
  };

  // The function passed to the Modal. Handles both Create and Update APIs.
  const handleSubmit = async (formValues) => {
    setSubmitting(true);
    setError('');

    try {
      if (editingDriver?.id) {
        await updateInternalDriver(editingDriver.id, formValues);
      } else {
        await createInternalDriver(formValues);
      }

      // If successful, clean up and refresh the data
      setShowForm(false);
      setEditingDriver(null);
      await load(); 
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to save internal driver'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Asks for confirmation, then hits the Delete API
  const handleDelete = async (driver) => {
    const label = driver?.name || `Driver #${driver?.id ?? ''}`;
    const ok = window.confirm(`Delete internal driver "${label}"?`);
    if (!ok) return;

    setError('');

    try {
      await deleteInternalDriver(driver.id);
      await load(); // Refresh table to remove the deleted driver
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to delete internal driver'
      );
    }
  };

  const totalCount = rows.length;
  const filteredCount = filtered.length;
  const filtersActive =
    !!query.trim() ||
    stateFilter !== 'all' ||
    String(siteFilter || 'all') !== 'all';
  // ==========================================
  // SECTION 5: UI / RENDER
  // ==========================================
  return (
    <div className="p-6">
      
      {/* 5A. Header and Search Bar */}
            <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Internal Drivers</h1>
          <div className="text-sm text-gray-500">
            Company-owned driver roster and dispatch control for UPS/company admins.
          </div>
        </div>

      <div className="flex flex-wrap items-end gap-3">
        {/* Current Site Filter */}
          <div className="flex flex-col">
          <label
            htmlFor="internal-current-site-filter"
            className="text-xs text-gray-500 mb-1"
          >
            Current Site Filter
          </label>
          <select
            id="internal-current-site-filter"
            name="internalCurrentSiteFilter"
            className="border rounded px-3 py-2 text-sm bg-white"
            value={siteFilter}
            onChange={handleSiteFilterChange}
          >
              <option value="all">All Sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          {/* Dispatch Site Target */}
          <div className="flex flex-col">
          <label
            htmlFor="internal-dispatch-site"
            className="text-xs text-gray-500 mb-1"
          >
            Dispatch Site
          </label>
          <select
            id="internal-dispatch-site"
            name="internalDispatchSite"
            className="border rounded px-3 py-2 text-sm bg-white border-blue-300"
            value={dispatchSiteId}
            onChange={handleDispatchSiteChange}
          >
              <option value="">Select site…</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="internal-state-filter"
              className="text-xs text-gray-500 mb-1"
            >
              State Filter
            </label>
            <select
              id="internal-state-filter"
              name="internalStateFilter"
              className="border rounded px-3 py-2 text-sm bg-white"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="all">All States</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="active">Active</option>
              <option value="requested">Requested</option>
              <option value="banned">Banned</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="internal-driver-search"
              className="text-xs text-gray-500 mb-1"
            >
              Search
            </label>
            <input
              id="internal-driver-search"
              name="internalDriverSearch"
              type="text"
              className="border rounded px-3 py-2 text-sm w-72 focus:outline-none focus:ring"
              placeholder="Search internal drivers…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
          >
            New internal driver
          </button>

          <button
            onClick={load}
            className="px-3 py-2 rounded border text-gray-700 hover:bg-gray-50 transition text-sm"
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* 5B. Stats Bar */}
      <div className="bg-white rounded-lg border p-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-800">{filteredCount}</span>
            <span className="mx-1 text-gray-400">of</span>
            <span className="font-semibold text-gray-800">{totalCount}</span>
            <span> internal drivers</span>

            {filtersActive && (
              <span className="ml-2 text-xs italic text-blue-600">(Filters active)</span>
            )}
          </div>
        </div>

        {/* Top-level page error banner */}
        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
            {error}
          </div>
        )}
      </div>

      {/* 5C. Data Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Current State</th>
                <th className="text-left px-4 py-3">Current Site</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {/* Loop through filtered data and render rows */}
              {filtered.map((driver) => {
                const resolvedState = resolveInternalDriverState(driver);
                const stateLabel = formatStateLabel(resolvedState);
                const currentSiteLabel = getCurrentSiteLabel(driver);
                const primaryAction = getPrimaryAction(driver);
                const isActionBusy = actionBusyId === driver.id;

                return (
                  <tr key={driver.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800">{driver.name || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {driver.submission_date
                          ? `Created: ${new Date(driver.submission_date).toLocaleString()}`
                          : ''}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">
                      {driver.vehicle_registration_number || '—'}
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      <div>{driver.phone || '—'}</div>
                      <div className="text-xs text-gray-500">{driver.email || '—'}</div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded border bg-gray-50 text-gray-700 border-gray-200">
                        {stateLabel}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {currentSiteLabel}
                    </td>

                    <td className="px-4 py-3">
                      <SourceBadge workforceSource={driver.workforce_source} />
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {primaryAction && (
                          <button
                            onClick={() => handlePrimaryAction(driver)}
                            disabled={isActionBusy}
                            className={`px-3 py-1 rounded transition ${primaryAction.className}`}
                          >
                            {isActionBusy ? 'Working…' : primaryAction.label}
                          </button>
                        )}

                        <button
                          onClick={() => openDetails(driver)}
                          className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-100 transition"
                        >
                          Details
                        </button>

                        <button
                          onClick={() => openEdit(driver)}
                          className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-100 transition"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(driver)}
                          className="px-3 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* 5D. Empty States */}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    {query.trim() || stateFilter !== 'all' || String(siteFilter || 'all') !== 'all' ? (
                      <>No internal drivers match the current filters.</>
                    ) : (
                      <>No internal drivers yet. Create your first internal driver.</>
                    )}

                    <div className="mt-3">
                      <button
                        onClick={openCreate}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                      >
                        New internal driver
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Loading internal drivers…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5E. The Modal Mount */}
      {/* Conditionally rendered, passing the handlers down to the child component */}
      {showForm && (
        <InternalDriverFormModal
          isOpen={showForm}
          onClose={closeForm}
          onSubmit={handleSubmit}
          driver={editingDriver}
          submitting={submitting}
        />
      )}
      {detailsDriver && (
        <DriverDetailsModal
          driver={detailsDriver}
          onClose={() => setDetailsDriver(null)}
          onMoveSuccess={load}
          refreshAssignmentsAfterMove={false}
        />
      )}
    </div>
  );
}
